/*
 * @Description: 上传封装（并发 + 总体进度单调 + 100% 保持可见）
 */

import axios from './axios.js';
import { translate } from './i18n/index.js';
import SparkMD5 from './lib/spark-md5.min.js';



// ---------------------------------------------------------------------------
/**
 * 获取url文件（如图片/视频）MD5
 * @param {String} url 资源地址（同源或 CORS 允许）
 * @returns {Promise<string>} MD5 值
 */
function getMD5(url) {
	return new Promise(async (resolve, reject) => {
		try {
			const response = await fetch(url)
			if (!response.ok) {
				reject(`Request failed: ${response.status}`);
				return;
			}

			const buffer = await response.arrayBuffer();
			const spark = new SparkMD5.ArrayBuffer();
			spark.append(buffer);
			const md5 = spark.end();
			resolve(md5);
		} catch (err) {
			reject(`Calculating md5 failed: ${err}`);
		}
	});
}
// ---------------------------------------------------------------------------

// ========== 常量 ==========
const HOLD_100_MS = 300; // 100% 后停留的毫秒数，避免直接 hide 看不到 100%

// ========== H5 普通上传（保留原功能） ==========
function uploadFile(file, data, url = '/OA/nxcloud/resource/uploadResource') {
	return new Promise((resolve, reject) => {
		if (!file) {
			reject(getApp().translate('PLEASE_SELECT_FILE'));
			return;
		}
		axios({
				method: 'post',
				url: getApp().globalData.Host + url,
				withCredentials: true,
				data,
				headers: { 'Content-Type': 'multipart/form-data' }
			})
			.then((res) => {
				if (res.data.result) {
					resolve(res.data);
					return;
				} else if (res.data.msg.includes('Login timeout')) {
					getApp().showModal(res.data.msg, getApp().translate('SYSTEM_PROMPT'), true, getApp().translate('OK'), (result) => {
						result.confirm && uni.navigateTo({ url: '/pages/login/login' });
					});
					return;
				}
				reject(res.data.msg);
			})
			.catch(reject);
	});
}

// ========== 小工具 ==========
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function createAbortController() { return new AbortController(); }

function calcFileMD5(file, chunkSize = 4 * 1024 * 1024) {
	return new Promise((resolve, reject) => {
		const spark = new SparkMD5.ArrayBuffer();
		const reader = new FileReader();
		const total = Math.ceil(file.size / chunkSize);
		let idx = 0;
		reader.onload = e => {
			spark.append(e.target.result);
			if (++idx < total) readNext();
			else resolve(spark.end());
		};
		reader.onerror = reject;

		function readNext() {
			const start = idx * chunkSize;
			const end = Math.min(file.size, start + chunkSize);
			reader.readAsArrayBuffer(file.slice(start, end));
		}
		readNext();
	});
}

/** 总体进度显示（0~1），带“全局单调不下降” */
function makeOverallLoadingReporter(_translate) {
	let lastShown01 = 0; // 显示过的最大值（0~1）
	let lastP = -1,
		lastTs = 0;

	function showPercent(p01) {
		const p01Mono = Math.max(lastShown01, p01); // 全局单调
		lastShown01 = p01Mono;

		const p = Math.max(0, Math.min(100, Math.round(p01Mono * 100)));
		const now = Date.now();
		if (p !== lastP && (p - lastP >= 1 || now - lastTs >= 200 || p === 100)) {
			lastP = p;
			lastTs = now;



			uni.showLoading({
				title: (_translate?.("UPLOADING") || "Uploading") + ` ${p}%`,
				mask: true
			});
		}
	}

	showPercent(0); // 初始 0%
	return showPercent;
}

// ========== 单文件上传：直传 / 分片 ==========
async function uploadSmallFile({ file, type, md5, signal, onProgress }) {
	const fd = new FormData();
	fd.append("file", file);
	fd.append("md5", md5);
	fd.append("type", String(type));

	const res = await axios({
		method: "post",
		url: getApp().globalData.Host + "/OA/kgalbum/res/upload",
		withCredentials: true,
		data: fd,
		headers: { "Content-Type": "multipart/form-data" },
		signal,
		onUploadProgress: (e) => {
			if (!onProgress || !e.total) return;
			onProgress(e.loaded / e.total); // 0~1（单文件）
		}
	}).then(r => r.data);

	if (!res.result) throw new Error(res.msg || "UPLOAD_FAILED");
	return res;
}

async function uploadLargeFile({
	file,
	type,
	signal,
	chunkSize = 4 * 1024 * 1024,
	concurrency = 4,
	retries = 2,
	onProgress
}) {
	const ext = (file.name.split(".").pop() || "").toLowerCase();
	const totalChunks = Math.ceil(file.size / chunkSize);
	const md5 = await calcFileMD5(file, chunkSize);
	if (signal?.aborted) throw new Error("aborted");

	const check = await axios.post(
		getApp().globalData.Host + "/OA/kgalbum/res/check-chunk", { md5, type, totalChunks, ext, size: file.size }, { withCredentials: true, signal }
	).then(r => r.data);

	if (!check.result) throw new Error(check.msg || "CHECK_FAILED");
	if (check.exists) { onProgress && onProgress(1); return check; }

	// 分片进度（分片内单调：取最大 loaded）
	const uploadedSet = new Set(check.uploaded || []);
	const chunkLoaded = new Map();
	const chunkSizeOf = (i) => Math.min(chunkSize, file.size - i * chunkSize);
	uploadedSet.forEach(i => chunkLoaded.set(i, chunkSizeOf(i)));

	const sumLoaded = () => {
		let s = 0;
		for (let i = 0; i < totalChunks; i++) s += Math.min(chunkSizeOf(i), chunkLoaded.get(i) || 0);
		return s;
	};

	const makeTask = (i) => async () => {
		if (uploadedSet.has(i)) return;
		const start = i * chunkSize;
		const end = Math.min(file.size, start + chunkSize);
		const blob = file.slice(start, end);

		for (let a = 0; a <= retries; a++) {
			try {
				const fd = new FormData();
				fd.append("md5", md5);
				fd.append("type", String(type));
				fd.append("chunkIndex", String(i));
				fd.append("totalChunks", String(totalChunks));
				fd.append("fileName", file.name);
				fd.append("ext", ext);
				fd.append("file", blob, `${file.name}.part${i}`);

				const r = await axios.post(
					getApp().globalData.Host + "/OA/kgalbum/res/upload-chunk",
					fd, {
						withCredentials: true,
						headers: { "Content-Type": "multipart/form-data" },
						signal,
						onUploadProgress: (e) => {
							if (!onProgress || !e.total) return;
							const prev = chunkLoaded.get(i) || 0;
							const cur = Math.min(blob.size, e.loaded);
							if (cur > prev) {
								chunkLoaded.set(i, cur);
								onProgress(sumLoaded() / file.size);
							}
						}
					}
				).then(r => r.data);

				if (!r.result) throw new Error(r.msg || "CHUNK_UPLOAD_FAILED");
				if ((chunkLoaded.get(i) || 0) < blob.size) {
					chunkLoaded.set(i, blob.size);
					onProgress && onProgress(sumLoaded() / file.size);
				}
				return; // 成功
			} catch (err) {
				if (signal?.aborted) throw err;
				if (a === retries) throw err;
				await new Promise(rs => setTimeout(rs, 400 * (a + 1)));
			}
		}
	};

	// 分片并发池
	const tasks = [];
	for (let i = 0; i < totalChunks; i++) tasks.push(makeTask(i));
	let cursor = 0;
	const workers = new Array(Math.min(concurrency, tasks.length)).fill(0).map(async () => {
		while (cursor < tasks.length) {
			const t = tasks[cursor++];
			await t();
		}
	});
	await Promise.all(workers);
	if (signal?.aborted) throw new Error("aborted");

	// 合并
	const merged = await axios.post(
		getApp().globalData.Host + "/OA/kgalbum/res/merge-chunk", { md5, type, fileName: file.name, ext }, { withCredentials: true, signal }
	).then(r => r.data);

	if (!merged.result) throw new Error(merged.msg || "MERGE_FAILED");
	onProgress && onProgress(1);
	return merged;
}

async function uploadAuto({ file, type, signal, onProgress, bigThreshold = 15 * 1024 * 1024 }) {
	if (file.size <= bigThreshold) {
		const md5 = await calcFileMD5(file);
		return uploadSmallFile({ file, type, md5, signal, onProgress });
	}
	return uploadLargeFile({ file, type, signal, onProgress });
}

/* ========== 多文件并行上传（文件级并发 + 单调进度 + 100%停留） ========== */
async function uploadFiles({
	files,
	type = 1,
	translate,
	picker,
	fileConcurrency = 3,
	continueOnError = true
}) {
	if (!files || !files.length) return { results: [], errors: [] };

	// 预建所有文件状态（用于全局求和）
	const state = Object.create(null); // uuid -> { size, loaded, done }
	let totalBytes = 0;
	for (const tf of files) {
		const size = tf?.file?.size || 0;
		state[tf.uuid] = { size, loaded: 0, done: false };
		totalBytes += size;
	}

	const showOverall = makeOverallLoadingReporter(translate);

	// 总体进度 = sum(loaded) / totalBytes（显示层已全局单调）
	const updateOverall = () => {
		const loaded = Object.values(state).reduce((s, f) => s + (f.loaded || 0), 0);
		const ratio = totalBytes ? (loaded / totalBytes) : 0;
		showOverall(ratio);
	};

	// 构造每个文件的任务（文件内单调：loaded = max(loaded, next)）
	const makeFileTask = (tf) => async () => {
		const file = tf.file;
		const uuid = tf.uuid;
		const st = state[uuid];
		const controller = createAbortController();

		const onProgress = (p01) => {
			const next = Math.max(0, Math.min(st.size, Math.round((p01 || 0) * st.size)));
			if (next > st.loaded) {
				st.loaded = next; // 单调不下降
				picker?.setExternalProgress?.(uuid, Math.round((st.loaded / st.size) * 100));
				updateOverall();
			}
		};

		try {
			const res = await uploadAuto({ file, type, signal: controller.signal, onProgress });
			st.loaded = st.size; // 文件完成，强制满值
			st.done = true;
			picker?.setExternalStatus?.(uuid, "success");
			updateOverall();
			return {
				ok: true,
				value: {
					res_id: res.id,
					url: res.url,
					type: res.type || (["mp4", "rmvb", "mov", "mkv", "flv", "avi"].includes((res.extname || "").toLowerCase()) ? "video" : "image"),
					extname: res.extname,
					name: res.file_name || file.name
				}
			};
		} catch (e) {
			st.done = true; // 失败也标记完成，但不回退 loaded
			picker?.setExternalStatus?.(uuid, "error", (e && e.message) || translate?.("UPLOAD_FAILED") || "Upload failed");
			updateOverall();
			if (!continueOnError) throw e;
			return { ok: false, error: e };
		}
	};

	// 文件级并发池
	const tasks = files.map(makeFileTask);
	const results = [];
	const errors = [];

	let cursor = 0;
	const workers = new Array(Math.min(fileConcurrency, tasks.length)).fill(0).map(async () => {
		while (cursor < tasks.length) {
			const idx = cursor++;
			try {
				const r = await tasks[idx]();
				if (r.ok) results.push(r.value);
				else errors.push(r.error);
			} catch (e) {
				errors.push(e);
				if (!continueOnError) break;
			}
		}
	});

	await Promise.all(workers).catch(() => { /* 已在内部记录 */ });

	// ——关键修复：确保“看到 100%”——
	showOverall(1); // 先把进度刷到 100%
	await sleep(HOLD_100_MS); // 停留片刻再关闭（避免看不到 100%）
	uni.hideLoading();

	return { results, errors };
}

// ---------------------------------------------------------------------------

export default {
	value: {},
	method: {
		getMD5,
		uploadFile,
		uploadFiles,
	}
};